import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from '@/components/ui/dialog';
import { Radio, RadioGroup } from '@/components/ui/radio';
import { toaster } from '@/components/ui/toaster';
import { useSwitchToFreePlanAction } from '@/hooks/actions/use-switch-to-free-plan-action';
import { Box, Button, List, Stack, Text } from '@chakra-ui/react';
import { PLAN_ENTITLEMENTS, PlanTier, type OrganizationBillingContext } from '@fluenceur/types';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatSettingsDate } from '../format-settings-date';

/** Searches the free plan counts per month, as its entitlements state them. */
const FREE_MONTHLY_SEARCHES = PLAN_ENTITLEMENTS[PlanTier.FREE].discoverySearches?.max ?? 0;

/**
 * The switch from a paid plan to the free plan: which programme stays active
 * (asked only when several are), then what changes, before anything happens.
 * The confirmation stays closed until a programme is chosen.
 */
export const SwitchToFreeDialog = ({
  planName,
  paidUntil = null,
  accessSource,
  canConfirm = true,
  programs = [],
  onClose,
}: {
  /** The paid plan being left, already translated ("Essentiel"). */
  planName: string;
  /**
   * End of the period already paid: the plan's limits stay until then (the
   * programmes are paused at that date). Null for a trial, which ends at once.
   */
  paidUntil?: string | null;
  accessSource?: OrganizationBillingContext['accessSource'];
  canConfirm?: boolean;
  programs?: OrganizationBillingContext['freePlanPrograms'];
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const locale = t('common.locale');
  const switchToFree = useSwitchToFreePlanAction();
  const retainsSpecificAccess = accessSource === 'legacy_brand_bypass';
  const mustChoose = !retainsSpecificAccess && programs.length > 1;
  const buttonKey = retainsSpecificAccess
    ? 'settings.subscription.switchToFree.retainedAccess.button'
    : 'settings.subscription.switchToFree.button';
  const [keepProgramId, setKeepProgramId] = useState<string | null>(null);

  const confirm = async () => {
    if (!canConfirm || switchToFree.isPending) return;
    try {
      const result = await switchToFree.mutateAsync({
        keepProgramId: retainsSpecificAccess
          ? null
          : mustChoose
            ? keepProgramId
            : (programs[0]?.id ?? null),
      });
      if (result.kind === 'scheduled') {
        toaster.success({
          title: t(
            retainsSpecificAccess
              ? 'settings.subscription.switchToFree.retainedAccess.scheduledTitle'
              : 'settings.subscription.switchToFree.scheduled.title'
          ),
          description: retainsSpecificAccess
            ? [
                t('settings.subscription.switchToFree.changes.subscriptionUntil', {
                  plan: planName,
                  date: formatSettingsDate(result.effectiveAt, locale),
                }),
                t('settings.subscription.switchToFree.retainedAccess.description'),
              ].join(' ')
            : t('settings.subscription.switchToFree.scheduled.description', {
                date: formatSettingsDate(result.effectiveAt, locale),
              }),
        });
        onClose();
        return;
      }
      if (retainsSpecificAccess) {
        toaster.success({
          title: t('settings.subscription.switchToFree.retainedAccess.appliedTitle'),
          description: t('settings.subscription.switchToFree.retainedAccess.description'),
        });
        onClose();
        return;
      }
      const programsDescription = !result.keptProgram
        ? null
        : result.pausedCount > 0
          ? t('settings.subscription.switchToFree.success.description', {
              program: result.keptProgram.name,
              count: result.pausedCount,
            })
          : t('settings.subscription.switchToFree.success.descriptionNoPause');
      const until = result.paidAccessEndsAt
        ? t('settings.subscription.switchToFree.success.until', {
            plan: planName,
            date: formatSettingsDate(result.paidAccessEndsAt, locale),
          })
        : null;
      const description = [programsDescription, until].filter(Boolean).join(' ');
      toaster.success({
        title: t('settings.subscription.switchToFree.success.title'),
        description: description || undefined,
      });
      onClose();
    } catch {
      toaster.error({ title: t('settings.subscription.switchToFree.error') });
    }
  };

  const changes = [
    ...(mustChoose
      ? [
          paidUntil
            ? t('settings.subscription.switchToFree.scheduled.programs', {
                date: formatSettingsDate(paidUntil, locale),
              })
            : t('settings.subscription.switchToFree.changes.programs'),
        ]
      : []),
    ...(retainsSpecificAccess
      ? [t('settings.subscription.switchToFree.retainedAccess.description')]
      : paidUntil
        ? [
            t('settings.subscription.switchToFree.scheduled.features', {
              date: formatSettingsDate(paidUntil, locale),
            }),
          ]
        : [
            t('settings.subscription.switchToFree.changes.automation'),
            t('settings.subscription.switchToFree.changes.profiles'),
            t('settings.subscription.switchToFree.changes.searches', {
              count: FREE_MONTHLY_SEARCHES,
            }),
          ]),
    paidUntil
      ? t('settings.subscription.switchToFree.changes.subscriptionUntil', {
          plan: planName,
          date: formatSettingsDate(paidUntil, locale),
        })
      : t('settings.subscription.switchToFree.changes.subscription', { plan: planName }),
  ];

  return (
    <DialogRoot
      open
      placement="center"
      onOpenChange={(details) => {
        if (!details.open) onClose();
      }}
    >
      <DialogContent maxW="520px" boxShadow="none">
        <DialogHeader>
          <DialogTitle>
            {t(
              retainsSpecificAccess
                ? 'settings.subscription.switchToFree.retainedAccess.button'
                : 'settings.subscription.switchToFree.title'
            )}
          </DialogTitle>
        </DialogHeader>
        <DialogCloseTrigger />
        <DialogBody>
          <Stack gap={5}>
            {mustChoose && (
              <Stack gap={3}>
                <Box>
                  <Text fontWeight="semibold">
                    {t('settings.subscription.switchToFree.programQuestion')}
                  </Text>
                  <Text fontSize="sm" color="fg.muted">
                    {t('settings.subscription.switchToFree.programHelp')}
                  </Text>
                </Box>
                <RadioGroup
                  value={keepProgramId}
                  onValueChange={({ value }) => setKeepProgramId(value)}
                >
                  <Stack gap={2}>
                    {programs.map((program) => (
                      <Radio key={program.id} value={program.id}>
                        <Text as="span" fontWeight="medium">
                          {program.name}
                        </Text>{' '}
                      </Radio>
                    ))}
                  </Stack>
                </RadioGroup>
              </Stack>
            )}
            <Stack gap={2}>
              <Text fontWeight="semibold">
                {t('settings.subscription.switchToFree.changesTitle')}
              </Text>
              <List.Root gap={2} ps={5} fontSize="sm" color="fg.muted">
                {changes.map((change) => (
                  <List.Item key={change}>{change}</List.Item>
                ))}
              </List.Root>
            </Stack>
          </Stack>
        </DialogBody>
        <DialogFooter
          flexDirection={{ base: 'column', md: 'row' }}
          alignItems={{ base: 'stretch', md: 'center' }}
        >
          <Button variant="outline" w={{ base: 'full', md: 'auto' }} onClick={onClose}>
            {t('settings.subscription.switchToFree.keep', { plan: planName })}
          </Button>
          <Button
            colorPalette="gray"
            w={{ base: 'full', md: 'auto' }}
            disabled={!canConfirm || (mustChoose && !keepProgramId)}
            loading={switchToFree.isPending}
            loadingText={t(
              retainsSpecificAccess
                ? 'settings.subscription.switchToFree.retainedAccess.pending'
                : 'settings.subscription.switchToFree.pending'
            )}
            onClick={confirm}
          >
            {t(buttonKey)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};
